import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBlogV3Draft from './blog-v3-draft-draft-posts.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKVideoV2ToRESTVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformSDKPageURLToRESTPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface DraftPost {
  /**
   * Draft post ID.
   * @readonly
   * @maxLength 38
   */
  _id?: string;
  /**
   * Draft post title.
   * @maxLength 200
   */
  title?: string;
  /**
   * Draft post excerpt.
   *
   * If no excerpt has been manually set, an excerpt is automatically generated from the post's text.
   * This can be retrieved using the `GENERATED_EXCERPT` fieldset.
   * @maxLength 500
   */
  excerpt?: string | null;
  /** Whether the draft post is marked as featured. */
  featured?: boolean | null;
  /**
   * Category IDs of the draft post.
   * @maxSize 10
   * @maxLength 38
   */
  categoryIds?: string[];
  /**
   * Draft post owner's member ID.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Hashtags in the post.
   * @maxSize 100
   * @maxLength 100
   */
  hashtags?: string[];
  /** Whether commenting on the draft post is enabled. */
  commentingEnabled?: boolean | null;
  /**
   * Estimated reading time of the draft post (calculated automatically).
   * @readonly
   */
  minutesToRead?: number;
  /** Image placed at the top of the blog page. */
  heroImage?: string;
  /**
   * Tag IDs the draft post is tagged with.
   * @maxSize 30
   * @maxLength 38
   */
  tagIds?: string[];
  /**
   * IDs of posts related to this draft post.
   * @maxSize 3
   * @maxLength 38
   */
  relatedPostIds?: string[];
  /**
   * Pricing plan IDs. Only relevant if a post is assigned to a specific pricing plan. See the Pricing Plans API for more info.
   * @maxSize 100
   * @format GUID
   */
  pricingPlanIds?: string[];
  /**
   * ID of the draft post's translations.
   *
   * All translations of a single post share the same `translationId`.
   * Available only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed.
   * @format GUID
   */
  translationId?: string | null;
  /**
   * Language the draft post is written in.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Draft Post rich content.
   *
   * <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">
   * <a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>
   * </widget>
   */
  richContent?: RichContent;
  /**
   * Status of the draft post.
   * @readonly
   */
  status?: StatusWithLiterals;
  /** Details of the draft post in review. Only relevant to posts submitted by guest writers. */
  moderationDetails?: ModerationDetails;
  /**
   * Indicates if there are changes made to the draft post that have not yet been published.
   * @readonly
   */
  hasUnpublishedChanges?: boolean;
  /**
   * Date the draft post was last edited.
   * @readonly
   */
  editedDate?: Date | null;
  /**
   * Date the draft post is scheduled to be published.
   * @readonly
   */
  scheduledPublishDate?: Date | null;
  /** Date the post was first published. */
  firstPublishedDate?: Date | null;
  /** SEO data. */
  seoData?: SeoSchema;
  /**
   * Draft post URL preview. What the URL will look like once the post is published.
   * @readonly
   */
  url?: string;
  /**
   * Date the draft post was first created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * SEO slug.
   * @maxLength 100
   */
  seoSlug?: string | null;
  /** Post cover media. */
  media?: Media;
  /** Number of paragraphs to display in a paid content preview for non-paying users. */
  previewTextParagraph?: number | null;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
  /** crop by height */
  height?: number | null;
  /** crop by width */
  width?: number | null;
}

export enum Origin {
  UNKNOWN = 'UNKNOWN',
  /** Changed by admin */
  ADMIN = 'ADMIN',
  /** Categories were changed */
  ADD_CATEGORIES = 'ADD_CATEGORIES',
  /** Saved automatically */
  AUTO_SAVE = 'AUTO_SAVE',
  /** Copied from template */
  COPY_TEMPLATE = 'COPY_TEMPLATE',
  /** Imported */
  IMPORT = 'IMPORT',
  /** Imported in bulk */
  IMPORT_BULK = 'IMPORT_BULK',
  /** Imported with html import */
  IMPORT_HTML = 'IMPORT_HTML',
  /** Patch import */
  IMPORT_PATCH = 'IMPORT_PATCH',
  /** Changed language */
  LANGUAGE_CHANGE = 'LANGUAGE_CHANGE',
  /** Saved manually */
  MANUAL_SAVE = 'MANUAL_SAVE',
  /** Affected by migration */
  MIGRATION = 'MIGRATION',
  /** Affected by moderation */
  MODERATION = 'MODERATION',
  /** Moved to trash */
  MOVE_TO_TRASH = 'MOVE_TO_TRASH',
  /** Pricing plans were changed */
  PRICING_PLANS_CHANGE = 'PRICING_PLANS_CHANGE',
  /** Was provisioned */
  PROVISION = 'PROVISION',
  /** Was published */
  PUBLISH = 'PUBLISH',
  /** Owner was reassigned */
  REASSIGN_OWNER = 'REASSIGN_OWNER',
  /** Was reblogged */
  REBLOG = 'REBLOG',
  /** Was restored */
  RESTORE = 'RESTORE',
  /** Reverted to draft */
  REVERT_TO_DRAFT = 'REVERT_TO_DRAFT',
  /** Was translated */
  TRANSLATION = 'TRANSLATION',
  /** Was unpublished */
  UNPUBLISH = 'UNPUBLISH',
  /** Was unscheduled */
  UNSCHEDULE = 'UNSCHEDULE',
  /** New edit session started which updated editing_session_id id */
  NEW_EDIT_SESSION = 'NEW_EDIT_SESSION',
  /** Was scheduled by Later */
  SCHEDULING_SERVICE_SCHEDULE = 'SCHEDULING_SERVICE_SCHEDULE',
  /** Was unscheduled by Later */
  SCHEDULING_SERVICE_UNSCHEDULE = 'SCHEDULING_SERVICE_UNSCHEDULE',
  /** Was published by Later */
  SCHEDULING_SERVICE_PUBLISH = 'SCHEDULING_SERVICE_PUBLISH',
  /** Was scheduled */
  SCHEDULE = 'SCHEDULE',
  /** Was removed from moderation */
  REMOVE_FROM_MODERATION = 'REMOVE_FROM_MODERATION',
  /** Was rejected from moderation */
  REJECT_FROM_MODERATION = 'REJECT_FROM_MODERATION',
  /** Was approved in moderation */
  APPROVE_IN_MODERATION = 'APPROVE_IN_MODERATION',
  /** Tag was deleted */
  DELETE_TAG = 'DELETE_TAG',
  /** Post was pinned */
  PIN = 'PIN',
  /** Post was unpinned */
  UNPIN = 'UNPIN',
  /** Saved automatically by AI tool. */
  AI_AUTO_SAVE = 'AI_AUTO_SAVE',
}

/** @enumType */
export type OriginWithLiterals =
  | Origin
  | 'UNKNOWN'
  | 'ADMIN'
  | 'ADD_CATEGORIES'
  | 'AUTO_SAVE'
  | 'COPY_TEMPLATE'
  | 'IMPORT'
  | 'IMPORT_BULK'
  | 'IMPORT_HTML'
  | 'IMPORT_PATCH'
  | 'LANGUAGE_CHANGE'
  | 'MANUAL_SAVE'
  | 'MIGRATION'
  | 'MODERATION'
  | 'MOVE_TO_TRASH'
  | 'PRICING_PLANS_CHANGE'
  | 'PROVISION'
  | 'PUBLISH'
  | 'REASSIGN_OWNER'
  | 'REBLOG'
  | 'RESTORE'
  | 'REVERT_TO_DRAFT'
  | 'TRANSLATION'
  | 'UNPUBLISH'
  | 'UNSCHEDULE'
  | 'NEW_EDIT_SESSION'
  | 'SCHEDULING_SERVICE_SCHEDULE'
  | 'SCHEDULING_SERVICE_UNSCHEDULE'
  | 'SCHEDULING_SERVICE_PUBLISH'
  | 'SCHEDULE'
  | 'REMOVE_FROM_MODERATION'
  | 'REJECT_FROM_MODERATION'
  | 'APPROVE_IN_MODERATION'
  | 'DELETE_TAG'
  | 'PIN'
  | 'UNPIN'
  | 'AI_AUTO_SAVE';

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
  CARD = 'CARD',
  TOC = 'TOC',
  SMART_BLOCK = 'SMART_BLOCK',
  SMART_BLOCK_CELL = 'SMART_BLOCK_CELL',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE'
  | 'CARD'
  | 'TOC'
  | 'SMART_BLOCK'
  | 'SMART_BLOCK_CELL';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
}

/** Background type */
export enum BackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals = BackgroundType | 'COLOR' | 'GRADIENT';

export interface Gradient {
  /** Gradient type. */
  type?: GradientTypeWithLiterals;
  /**
   * Color stops for the gradient.
   * @maxSize 1000
   */
  stops?: Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number | null;
  /**
   * Horizontal center position for radial gradient (0-100).
   * @max 100
   */
  centerX?: number | null;
  /**
   * Vertical center position for radial gradient (0-100).
   * @max 100
   */
  centerY?: number | null;
}

/** Gradient type. */
export enum GradientType {
  /** Linear gradient. */
  LINEAR = 'LINEAR',
  /** Radial gradient. */
  RADIAL = 'RADIAL',
}

/** @enumType */
export type GradientTypeWithLiterals = GradientType | 'LINEAR' | 'RADIAL';

/** A single color stop in the gradient. */
export interface Stop {
  /**
   * Stop color as hex value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Stop position (0-1). */
  position?: number | null;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

/** Background styling (color or gradient) */
export interface Background {
  /** Background type. */
  type?: BackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /**
   * Deprecated: Use `backgroundHover` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
  /** Background styling (color or gradient). */
  background?: Background;
  /** Background styling for hover state (color or gradient). */
  backgroundHover?: Background;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
}

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
}

export interface V1Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface Image {
  /** Image file details. */
  media?: V1Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: V1Media;
  /** Video thumbnail file details. */
  thumbnail?: V1Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Rendered heading level for SEO/accessibility, overrides the HTML tag when set. */
  renderedLevel?: number | null;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
  AI_WIDGET = 'AI_WIDGET',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'HTML'
  | 'ADSENSE'
  | 'AI'
  | 'AI_WIDGET';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: V1Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @format COLOR_HEX
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @format COLOR_HEX
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: V1Media;
}

export interface PollSettings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum PollDesignBackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type PollDesignBackgroundTypeWithLiterals =
  | PollDesignBackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface BackgroundGradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @format COLOR_HEX
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @format COLOR_HEX
   */
  lastColor?: string | null;
}

export interface PollDesignBackground
  extends PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: V1Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
  /** Background type. For each option, include the relevant details. */
  type?: PollDesignBackgroundTypeWithLiterals;
}

/** @oneof */
export interface PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: V1Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: PollDesignBackground;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: V1Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: PollSettings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: V1Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: V1Media;
  /** Video thumbnail details. */
  thumbnail?: V1Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Table's alternative text. */
  altText?: string | null;
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: V1Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: V1Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: LayoutDataBackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Deprecated: Use `backdrop` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backdropColor?: string | null;
  /** Backdrop image. */
  backdropImage?: LayoutDataBackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is displayed as a banner. */
  banner?: Banner;
  /** Background styling (color or gradient). */
  background?: LayoutDataBackground;
  /** Backdrop styling (color or gradient). */
  backdrop?: Backdrop;
}

export enum ImageScalingScaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ImageScalingScalingWithLiterals =
  | ImageScalingScaling
  | 'AUTO'
  | 'CONTAIN'
  | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export enum BannerOrigin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type BannerOriginWithLiterals = BannerOrigin | 'IMAGE' | 'LAYOUT';

/** Background type */
export enum LayoutDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type LayoutDataBackgroundTypeWithLiterals =
  | LayoutDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Backdrop type */
export enum BackdropType {
  /** Solid color backdrop */
  COLOR = 'COLOR',
  /** Gradient backdrop */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackdropTypeWithLiterals = BackdropType | 'COLOR' | 'GRADIENT';

export interface LayoutDataBackgroundImage {
  /** Background image. */
  media?: V1Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ImageScalingScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: BannerOriginWithLiterals;
}

/** Background styling (color or gradient) */
export interface LayoutDataBackground {
  /** Background type. */
  type?: LayoutDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Backdrop styling (color or gradient) */
export interface Backdrop {
  /** Backdrop type. */
  type?: BackdropTypeWithLiterals;
  /**
   * Backdrop color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: V1Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface CardData {
  /** Background styling (color or gradient). */
  background?: CardDataBackground;
  /** Background image. */
  backgroundImage?: BackgroundImage;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePositionPosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionPositionWithLiterals =
  | ImagePositionPosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background type */
export enum CardDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type CardDataBackgroundTypeWithLiterals =
  | CardDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Background styling (color or gradient) */
export interface CardDataBackground {
  /** Background type. */
  type?: CardDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface BackgroundImage {
  /** Background image. */
  media?: V1Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionPositionWithLiterals;
}

export interface TocData {
  /** Heading levels included in the table of contents. Default: [1, 2, 3, 4, 5, 6]. */
  includedHeadings?: number[];
  /** List style. Default: PLAIN. */
  listStyle?: ListStyleWithLiterals;
  /** Optional override for the font size in pixels. */
  fontSize?: number | null;
  /** Optional override for the vertical spacing between items in pixels. */
  itemSpacing?: number | null;
  /**
   * Optional override for the text color.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Indentation style. Default: NESTED. */
  indentation?: IndentationWithLiterals;
}

/** List style. */
export enum ListStyle {
  /** No markers (default) */
  PLAIN = 'PLAIN',
  /** Numbered list */
  NUMBERED = 'NUMBERED',
  /** Alphabetic letters */
  LETTERS = 'LETTERS',
  /** Roman numerals */
  ROMAN = 'ROMAN',
  /** Bulleted list */
  BULLETED = 'BULLETED',
  /** Alphabetical index */
  ALPHABETICAL_INDEX = 'ALPHABETICAL_INDEX',
  /** Alphabetical index (compact top-row only) */
  ALPHABETICAL_INDEX_COMPACT = 'ALPHABETICAL_INDEX_COMPACT',
}

/** @enumType */
export type ListStyleWithLiterals =
  | ListStyle
  | 'PLAIN'
  | 'NUMBERED'
  | 'LETTERS'
  | 'ROMAN'
  | 'BULLETED'
  | 'ALPHABETICAL_INDEX'
  | 'ALPHABETICAL_INDEX_COMPACT';

/** Indentation style. */
export enum Indentation {
  /** Sub-headings indented under parents (default) */
  NESTED = 'NESTED',
  /** All items at the same level */
  FLAT = 'FLAT',
}

/** @enumType */
export type IndentationWithLiterals = Indentation | 'NESTED' | 'FLAT';

/** Data for a smart block node. */
export interface SmartBlockData {
  /** The type of the smart block. */
  type?: SmartBlockDataTypeWithLiterals;
  /** Layout orientation. HORIZONTAL or VERTICAL. Optional for variants with fixed orientation. */
  orientation?: string | null;
  /** Column size controlling cells per row. */
  columnSize?: ColumnSizeWithLiterals;
  /** Border color (for SOLID_JOINED_BOXES variant). */
  borderColor?: string | null;
  /** Border width in pixels (for SOLID_JOINED_BOXES variant). */
  borderWidth?: number | null;
  /** Border radius in pixels (for SOLID_JOINED_BOXES variant). */
  borderRadius?: number | null;
}

/** Layout type of the smart block */
export enum SmartBlockDataType {
  /** Grid-based layouts with solid box items containing title, body, and icon/image. */
  SOLID_BOXES = 'SOLID_BOXES',
  /** Numbered boxes. */
  NUMBERED_BOXES = 'NUMBERED_BOXES',
  /** Statistics display with large numbers/values. */
  STATS = 'STATS',
  /** Statistics with circular visual elements. */
  CIRCLE_STATS = 'CIRCLE_STATS',
  /** Staggered/zigzag grid layout with alternating box positions. */
  SOLID_BOXES_ALTERNATING = 'SOLID_BOXES_ALTERNATING',
  /** Grid layout with boxes visually joined (no gaps, shared container border). */
  SOLID_JOINED_BOXES = 'SOLID_JOINED_BOXES',
  /** Transparent cells with only a left side line. */
  SIDE_LINE_TEXT = 'SIDE_LINE_TEXT',
  /** Transparent cells with only a top line. */
  TOP_LINE_TEXT = 'TOP_LINE_TEXT',
  /** Outlined boxes with a numbered/icon circle at the top. */
  OUTLINE_BOXES_WITH_TOP_CIRCLE = 'OUTLINE_BOXES_WITH_TOP_CIRCLE',
  /** Large icon bullets with text content. */
  BIG_BULLETS = 'BIG_BULLETS',
  /** Small dot bullets with text content. */
  SMALL_BULLETS = 'SMALL_BULLETS',
  /** Arrow icon bullets with text content. */
  ARROW_BULLETS = 'ARROW_BULLETS',
  /** Process steps with numbered/icon labels above a horizontal line. */
  PROCESS_STEPS = 'PROCESS_STEPS',
  /** Statistics with bar visual elements. */
  BAR_STATS = 'BAR_STATS',
}

/** @enumType */
export type SmartBlockDataTypeWithLiterals =
  | SmartBlockDataType
  | 'SOLID_BOXES'
  | 'NUMBERED_BOXES'
  | 'STATS'
  | 'CIRCLE_STATS'
  | 'SOLID_BOXES_ALTERNATING'
  | 'SOLID_JOINED_BOXES'
  | 'SIDE_LINE_TEXT'
  | 'TOP_LINE_TEXT'
  | 'OUTLINE_BOXES_WITH_TOP_CIRCLE'
  | 'BIG_BULLETS'
  | 'SMALL_BULLETS'
  | 'ARROW_BULLETS'
  | 'PROCESS_STEPS'
  | 'BAR_STATS';

/** Column size controlling how many cells appear per row. */
export enum ColumnSize {
  /** Up to 4 cells in a row. */
  SMALL = 'SMALL',
  /** Up to 3 cells in a row (default). */
  MEDIUM = 'MEDIUM',
  /** Up to 2 cells in a row. */
  LARGE = 'LARGE',
  /** 1 cell in a row. */
  EXTRA_LARGE = 'EXTRA_LARGE',
}

/** @enumType */
export type ColumnSizeWithLiterals =
  | ColumnSize
  | 'SMALL'
  | 'MEDIUM'
  | 'LARGE'
  | 'EXTRA_LARGE';

/** Data for a smart block cell node. */
export interface SmartBlockCellData {
  /** Optional label text for the cell (e.g., for stats variants). */
  label?: string | null;
  /** Shape file details. */
  shape?: V1Media;
  /** Border color of the cell. */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /** The type of the parent smart block (must match parent). */
  type?: SmartBlockDataTypeWithLiterals;
  /** Accent color for non-background variants (e.g., line, bullet, label color). */
  accentColor?: string | null;
  /** Background color for background-based variants (SOLID_BOXES, SOLID_BOXES_ALTERNATING, SOLID_JOINED_BOXES). */
  backgroundColor?: string | null;
  /** Shape fill color as a hexadecimal value. */
  shapeColor?: string | null;
}

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  _id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export enum Status {
  UNKNOWN = 'UNKNOWN',
  /** Status indicating the draft post is published. */
  PUBLISHED = 'PUBLISHED',
  /** Status indicating the draft post is unpublished. */
  UNPUBLISHED = 'UNPUBLISHED',
  /** Status indicating the draft post is scheduled for publication. */
  SCHEDULED = 'SCHEDULED',
  /** Status indicating the draft post is deleted. */
  DELETED = 'DELETED',
  /** Status indicating the draft post is in review. */
  IN_REVIEW = 'IN_REVIEW',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UNKNOWN'
  | 'PUBLISHED'
  | 'UNPUBLISHED'
  | 'SCHEDULED'
  | 'DELETED'
  | 'IN_REVIEW';

export interface ModerationDetails {
  /**
   * Member ID of the person submitting the draft post for review.
   * @format GUID
   */
  submittedBy?: string;
  /** Date the post was submitted for review. */
  submittedDate?: Date | null;
  /** Status indicating whether the submission was approved or rejected by the moderator. */
  status?: ModerationStatusStatusWithLiterals;
  /**
   * Member ID of the person who approved or rejected the post.
   * @format GUID
   */
  moderatedBy?: string | null;
  /** Date the post was approved or rejected. */
  moderationDate?: Date | null;
}

export enum ModerationStatusStatus {
  UNKNOWN = 'UNKNOWN',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/** @enumType */
export type ModerationStatusStatusWithLiterals =
  | ModerationStatusStatus
  | 'UNKNOWN'
  | 'APPROVED'
  | 'REJECTED';

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface Media extends MediaMediaOneOf {
  /** Wix Media details. */
  wixMedia?: WixMedia;
  /** Embed media details. */
  embedMedia?: EmbedMedia;
  /** Whether cover media is displayed. */
  displayed?: boolean;
  /** Whether custom cover media has been specified. If `false`, the first media item in the post's content serves as cover media. */
  custom?: boolean;
  /**
   * Media alternative text.
   * @minLength 1
   * @maxLength 1000
   */
  altText?: string | null;
}

/** @oneof */
export interface MediaMediaOneOf {
  /** Wix Media details. */
  wixMedia?: WixMedia;
  /** Embed media details. */
  embedMedia?: EmbedMedia;
}

export interface WixMedia {
  /** Image details. */
  image?: string;
  /** Video details. */
  videoV2?: string;
}

export interface VideoResolution {
  /** Video URL. */
  url?: string;
  /** Video height. */
  height?: number;
  /** Video width. */
  width?: number;
  /** Video format for example, mp4, hls. */
  format?: string;
}

export interface EmbedMedia {
  /** Thumbnail details. */
  thumbnail?: EmbedThumbnail;
  /** Video details. */
  video?: EmbedVideo;
}

export interface EmbedThumbnail {
  /**
   * Thumbnail url.
   * @maxLength 2000
   */
  url?: string;
  /** Thumbnail width. */
  width?: number;
  /** Thumbnail height. */
  height?: number;
}

export interface EmbedVideo {
  /**
   * Video url.
   * @maxLength 2000
   */
  url?: string;
  /** Video width. */
  width?: number;
  /** Video height. */
  height?: number;
}

export interface DraftPostTranslation {
  /**
   * Post ID.
   * @format GUID
   */
  _id?: string;
  /** Post status. */
  status?: StatusWithLiterals;
  /**
   * Language the post is written in.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Post slug. For example, 'post-slug'.
   * @maxLength 100
   */
  slug?: string | null;
  /** SEO data. */
  seoData?: SeoSchema;
  /** Post URL. */
  url?: string;
}

export interface DraftCategoriesUpdated {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId?: string;
  /**
   * Current categories of the draft.
   * @maxSize 10
   * @format GUID
   */
  categories?: string[];
  /**
   * Previous categories of the draft.
   * @maxSize 10
   * @format GUID
   */
  previousCategories?: string[];
}

export interface DraftTagsUpdated {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId?: string;
  /**
   * Current tags of the draft.
   * @maxSize 30
   * @format GUID
   */
  tags?: string[];
  /**
   * Previous tags of the draft.
   * @maxSize 30
   * @format GUID
   */
  previousTags?: string[];
}

export interface GetDraftPostTotalsRequest {
  /**
   * Group results by fields (defaults to grouping by status).
   * If, for example, grouping by language is passed, language values in response will be filled.
   * If, for example, grouping by language is not passed, null values will be filled in language field in response.
   * @maxSize 10
   */
  groupBy?: TotalDraftPostsGroupingFieldWithLiterals[];
  /**
   * Optional language filter by provided language code. Useful in multilingual context.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
}

export enum TotalDraftPostsGroupingField {
  /** Groups results by status. */
  STATUS = 'STATUS',
  /** Groups results by language. */
  LANGUAGE = 'LANGUAGE',
}

/** @enumType */
export type TotalDraftPostsGroupingFieldWithLiterals =
  | TotalDraftPostsGroupingField
  | 'STATUS'
  | 'LANGUAGE';

export interface GetDraftPostTotalsResponse {
  /** Draft post totals. */
  totalDraftPosts?: TotalDraftPosts[];
}

export interface TotalDraftPosts {
  /** Draft post totals in that group. */
  total?: number;
  /** Draft post status (only has value when grouping by status, otherwise null). */
  status?: StatusWithLiterals;
  /**
   * Draft post language code (only has value when grouping by language, otherwise null).
   * @format LANGUAGE_TAG
   */
  language?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface InitialDraftPostsCopied {
  /** Number of draft posts copied. */
  count?: number;
}

export interface CreateDraftPostRequest {
  /** Draft post to create. */
  draftPost: DraftPost;
  /**
   * Whether the draft post should be published on creation.
   *
   * Default: `false`
   */
  publish?: boolean;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  /** Manually saved. */
  MANUAL = 'MANUAL',
  /** Triggered by autosave. */
  AUTO_SAVE = 'AUTO_SAVE',
  /** Copied from template during provisioning flow. */
  PROVISIONING = 'PROVISIONING',
  /** Imported from another blog. */
  IMPORT = 'IMPORT',
  /** Triggered by autosave when post created by AI tool. */
  AI_AUTO_SAVE = 'AI_AUTO_SAVE',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'MANUAL'
  | 'AUTO_SAVE'
  | 'PROVISIONING'
  | 'IMPORT'
  | 'AI_AUTO_SAVE';

export enum Field {
  /** Unknown field. */
  UNKNOWN = 'UNKNOWN',
  /** Includes draft post preview URL. */
  URL = 'URL',
  /** Includes content field. */
  CONTENT = 'CONTENT',
  /** Includes rich content field. */
  RICH_CONTENT = 'RICH_CONTENT',
  /** If the user has not set excerpt, returns the one autogenerated from content. */
  GENERATED_EXCERPT = 'GENERATED_EXCERPT',
}

/** @enumType */
export type FieldWithLiterals =
  | Field
  | 'UNKNOWN'
  | 'URL'
  | 'CONTENT'
  | 'RICH_CONTENT'
  | 'GENERATED_EXCERPT';

export interface CreateDraftPostResponse {
  /** Created draft post info. */
  draftPost?: DraftPost;
}

export interface BulkCreateDraftPostsRequest {
  /**
   * Draft posts to create.
   * @minSize 1
   * @maxSize 20
   */
  draftPosts: DraftPost[];
  /** Whether the draft post should be published after creation. */
  publish?: boolean;
  /** Whether to return the full created draft post entities in the response. */
  returnFullEntity?: boolean;
  /**
   * List of draft post fields to be included in the response if the entities are present.
   * Base default fieldset returns all core draft post properties (all properties that are not a supported fieldset value).
   * For example,  when `URL` fieldset is selected, returned draft post will include the set of base properties and the draft post's preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface BulkCreateDraftPostsResponse {
  /** Draft posts created by bulk action. */
  results?: BulkDraftPostResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDraftPostResult {
  /** Bulk actions metadata for draft post. */
  itemMetadata?: ItemMetadata;
  /** Optional full draft post. */
  item?: DraftPost;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateDraftPostsRequest {
  /**
   * Draft posts to update.
   * @minSize 1
   * @maxSize 20
   */
  draftPosts?: MaskedDraftPosts[];
  /**
   * Action to perform on the posts.
   *
   * Default: `UPDATE`
   */
  action?: ActionWithLiterals;
  /** Posts' scheduled publish date when `action` is set to `UPDATE_SCHEDULE`. */
  scheduledPublishDate?: Date | null;
  /** Whether to return the full updated draft post entities in the response. */
  returnFullEntity?: boolean;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface MaskedDraftPosts {
  /** Draft post */
  draftPost?: DraftPost;
  /** Field mask of fields to update. */
  fieldMask?: string[];
}

export enum Action {
  /** Update the draft post. */
  UPDATE = 'UPDATE',
  /** Update and publish a draft post. */
  UPDATE_PUBLISH = 'UPDATE_PUBLISH',
  /** Update the draft post and schedule a publish date. */
  UPDATE_SCHEDULE = 'UPDATE_SCHEDULE',
  /** Update the post and revert it to draft. */
  UPDATE_REVERT_TO_DRAFT = 'UPDATE_REVERT_TO_DRAFT',
  /** Update the draft post and cancel the scheduled publish date. */
  UPDATE_CANCEL_SCHEDULE = 'UPDATE_CANCEL_SCHEDULE',
  /** Update and reject draft post. */
  UPDATE_REJECT = 'UPDATE_REJECT',
  /** Update a post that's already been published. This creates and updates a draft version of the post. The original post is still published. */
  UPDATE_PUBLICATION = 'UPDATE_PUBLICATION',
}

/** @enumType */
export type ActionWithLiterals =
  | Action
  | 'UPDATE'
  | 'UPDATE_PUBLISH'
  | 'UPDATE_SCHEDULE'
  | 'UPDATE_REVERT_TO_DRAFT'
  | 'UPDATE_CANCEL_SCHEDULE'
  | 'UPDATE_REJECT'
  | 'UPDATE_PUBLICATION';

export interface BulkUpdateDraftPostsResponse {
  /** Draft posts updated by bulk action. */
  results?: BulkDraftPostResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DraftPostOwnerChanged {}

export interface ListDeletedDraftPostsRequest {
  /**
   * Language filter.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * If omitted, deleted draft posts in all languages are returned.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Sorting options.
   *
   * Default: `EDITING_DATE_DESCENDING`
   */
  sort?: GetDraftPostsSortWithLiterals;
  /** Pagination options. */
  paging?: BlogPaging;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /**
   * Draft post ids.
   * @maxSize 100
   * @format GUID
   */
  draftPostIds?: string[];
}

export enum GetDraftPostsSort {
  /** Sort by `editedDate` in descending order. */
  EDITING_DATE_DESC = 'EDITING_DATE_DESC',
  /** Sort by `editedDate` in ascending order. */
  EDITING_DATE_ASC = 'EDITING_DATE_ASC',
}

/** @enumType */
export type GetDraftPostsSortWithLiterals =
  | GetDraftPostsSort
  | 'EDITING_DATE_DESC'
  | 'EDITING_DATE_ASC';

export interface BlogPaging {
  /**
   * Number of items to skip in the current sort order.
   *
   *
   * Default: `0`
   */
  offset?: number;
  /**
   * Number of items to return.
   *
   *
   * Default:`50`
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Pointer to the next or previous page in the list of results.
   * @maxLength 2000
   */
  cursor?: string | null;
}

export interface ListDeletedDraftPostsResponse {
  /** List of draft posts. */
  draftPosts?: DraftPost[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 2000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 2000
   */
  prev?: string | null;
}

export interface GetDraftPostRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId: string;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface GetDraftPostResponse {
  /** Draft post info. */
  draftPost?: DraftPost;
}

export interface UpdateDraftPostContentRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId?: string;
  /** Change origin. */
  changeOrigin?: OriginWithLiterals;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /** Draft Post rich content. */
  richContent?: RichContent;
}

export interface UpdateDraftPostContentResponse {
  /** Updated draft post info. */
  draftPost?: DraftPost;
}

export interface UpdateDraftPostRequest {
  /** Draft post to update. */
  draftPost: DraftPost;
  /**
   * Action to perform on the post.
   *
   * Default: `UPDATE`
   */
  action?: ActionWithLiterals;
  /** Post publish schedule date if `action` is set to `UPDATE_SCHEDULE`. */
  scheduledPublishDate?: Date | null;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface UpdateDraftPostResponse {
  /** Updated draft post info. */
  draftPost?: DraftPost;
}

export interface DeleteDraftPostRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId: string;
  /**
   * Whether to bypass the trash bin and delete the post permanently.
   *
   * Default: `false`
   */
  permanent?: boolean;
}

export interface DeleteDraftPostResponse {}

export interface RemoveFromTrashBinRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId: string;
}

export interface RemoveFromTrashBinResponse {}

export interface BulkDeleteDraftPostsRequest {
  /**
   * Post IDs.
   * @minSize 1
   * @maxSize 100
   * @maxLength 38
   */
  postIds: string[];
  /** Should delete bypassing the trash-bin. */
  permanent?: boolean;
}

export interface BulkDeleteDraftPostsResponse {
  /** Bulk action results. */
  results?: BulkDraftPostResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ListDraftPostsRequest {
  /**
   * Draft post status filter.
   *
   * If omitted, draft posts with all statuses are returned.
   */
  status?: StatusWithLiterals;
  /**
   * Language filter.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * If omitted, draft posts in all languages are returned.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Sort options.
   *
   * Default: `EDITING_DATE_DESCENDING`
   */
  sort?: GetDraftPostsSortWithLiterals;
  /** Pagination options. */
  paging?: BlogPaging;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface ListDraftPostsResponse {
  /** List of draft posts. */
  draftPosts?: DraftPost[];
  /** Details on the paged set of results returned. */
  metaData?: MetaData;
}

export interface MetaData {
  /** Number of items returned in this response. */
  count?: number;
  /** Requested offset. */
  offset?: number;
  /** Total number of items that match the query. */
  total?: number;
  /**
   * Pointer to the next or previous page in the list of results.
   * @maxLength 2000
   */
  cursor?: string | null;
}

export interface GetDeletedDraftPostRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId: string;
}

export interface GetDeletedDraftPostResponse {
  /** Draft post info. */
  draftPost?: DraftPost;
}

export interface RestoreFromTrashBinRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId: string;
}

export interface RestoreFromTrashBinResponse {
  /** Restored draft post info. */
  draftPost?: DraftPost;
}

export interface QueryDraftPostsRequest {
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /** Query options. */
  query?: PlatformQuery;
}

export interface PlatformQuery extends PlatformQueryPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 3
   */
  sort?: Sorting[];
}

/** @oneof */
export interface PlatformQueryPagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 2000
   */
  cursor?: string | null;
}

export interface QueryDraftPostsResponse {
  /** List of posts. */
  draftPosts?: DraftPost[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PublishDraftPostRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId: string;
}

export interface PublishDraftPostResponse {
  /**
   * Published post ID.
   * @format GUID
   */
  postId?: string;
}

export interface UnpublishPostRequest {
  /**
   * Draft post ID.
   * @maxLength 38
   */
  draftPostId?: string;
}

export interface UnpublishPostResponse {}

export interface GetDraftPostCountsRequest {
  /**
   * Tag IDs to count draft posts for.
   * @maxSize 50
   * @format GUID
   */
  tagIds?: string[];
  /**
   * Category IDs to count draft posts for.
   * @maxSize 50
   * @format GUID
   */
  categoryIds?: string[];
}

export interface GetDraftPostCountsResponse {
  /** Draft post counts per tag. */
  tagPostCounts?: DraftPostCount[];
  /** Draft post counts per category. */
  categoryPostCounts?: DraftPostCount[];
}

export interface DraftPostCount {
  /**
   * Tag or Category ID.
   * @format GUID
   */
  _id?: string;
  /** Number of posts. */
  postCount?: number;
}

export interface TranslateDraftRequest {
  /**
   * Source post or draft ID
   * @format GUID
   */
  postId?: string;
  /**
   * Translation language
   * @format LANGUAGE_TAG
   */
  language?: string;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface TranslateDraftResponse {
  /** Draft post. */
  draftPost?: DraftPost;
}

export interface IsDraftPostAutoTranslatableRequest {
  /**
   * Source post or draft ID.
   * @format GUID
   */
  draftPostId?: string;
}

export interface IsDraftPostAutoTranslatableResponse {
  /**
   * Source draft post ID.
   * @format GUID
   */
  draftPostId?: string;
  /** Indicates if enough machine translation credits are available for the draft post translation. */
  translatable?: boolean;
  /** Draft post title word count. */
  titleWordCount?: number;
  /** Draft post content word count. */
  contentWordCount?: number;
  /** Word credits available for auto translation. */
  availableAutoTranslateWords?: number;
  /** Word credits available after auto translation would be done. */
  availableAutoTranslateWordsAfter?: number;
  /** Content text character count. */
  contentTextCharacterCount?: number;
}

export interface UpdateDraftPostLanguageRequest {
  /**
   * Source draft post ID
   * @format GUID
   */
  postId?: string;
  /**
   * New language to replace to
   * @minLength 2
   * @format LANGUAGE_TAG
   */
  language?: string;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface UpdateDraftPostLanguageResponse {
  /** Draft post */
  draftPost?: DraftPost;
}

export interface BulkRevertToUnpublishedRequest {
  /**
   * Source post IDs.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  postIds?: string[];
  /** Should full draft post be returned. */
  returnFullEntity?: boolean;
}

export interface BulkRevertToUnpublishedResponse {
  /** Bulk action results. */
  results?: BulkDraftPostResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkRejectDraftPostRequest {
  /**
   * Source post IDs.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  postIds?: string[];
  /** Should full draft post be returned. */
  returnFullEntity?: boolean;
}

export interface BulkRejectDraftPostResponse {
  /** Bulk action results. */
  results?: BulkDraftPostResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface RevertToUnpublishedRequest {
  /**
   * Source post ID.
   * @format GUID
   */
  postId?: string;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface RevertToUnpublishedResponse {
  /** Updated post draft. */
  draftPost?: DraftPost;
}

export interface RejectDraftPostRequest {
  /**
   * Source post ID.
   * @format GUID
   */
  postId?: string;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface RejectDraftPostResponse {
  /** Draft post. */
  draftPost?: DraftPost;
}

export interface ApproveDraftPostRequest {
  /**
   * Source post ID.
   * @format GUID
   */
  postId?: string;
  /**
   * Scheduled publish date if should be not immediately published.
   * @maxLength 24
   */
  scheduledPublishDate?: string | null;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface ApproveDraftPostResponse {
  /** Updated post draft. */
  draftPost?: DraftPost;
}

export interface MarkPostAsInModerationRequest {
  /**
   * Source post ID.
   * @maxLength 38
   */
  postId?: string;
  /**
   * List of draft post fields to be included if entities are present in the response.
   * Base fieldset, which is default, will return all core draft post properties.
   * Example: When URL fieldset is selected, returned draft post will have a set of base properties and draft post preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

export interface MarkPostAsInModerationResponse {
  /** Updated post draft. */
  draftPost?: DraftPost;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface DraftCreatedEnvelope {
  entity: DraftPost;
  metadata: EventMetadata;
}

/**
 * Triggered when a draft post is created.
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionScope Read Blog
 * @permissionScopeId SCOPE.DC-BLOG.READ-BLOGS
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionId BLOG.READ_ANY_DRAFT
 * @permissionId BLOG.READ-PUBLICATION
 * @webhook
 * @eventType wix.blog.v3.draft_created
 * @serviceIdentifier com.wixpress.npm.communities.platformized.blog.v3.DraftPostService
 * @slug created
 */
export declare function onDraftCreated(
  handler: (event: DraftCreatedEnvelope) => void | Promise<void>
): void;

export interface DraftDeletedEnvelope {
  entity: DraftPost;
  metadata: EventMetadata;
}

/**
 * Triggered when a draft post is deleted.
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionScope Read Blog
 * @permissionScopeId SCOPE.DC-BLOG.READ-BLOGS
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionId BLOG.READ_ANY_DRAFT
 * @permissionId BLOG.READ-PUBLICATION
 * @webhook
 * @eventType wix.blog.v3.draft_deleted
 * @serviceIdentifier com.wixpress.npm.communities.platformized.blog.v3.DraftPostService
 * @slug deleted
 */
export declare function onDraftDeleted(
  handler: (event: DraftDeletedEnvelope) => void | Promise<void>
): void;

export interface DraftUpdatedEnvelope {
  entity: DraftPost;
  metadata: EventMetadata;
}

/**
 * Triggered when a draft post is updated.
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionScope Read Blog
 * @permissionScopeId SCOPE.DC-BLOG.READ-BLOGS
 * @permissionScope Manage Blog
 * @permissionScopeId SCOPE.DC-BLOG.MANAGE-BLOG
 * @permissionScope Read Draft Blog Posts
 * @permissionScopeId SCOPE.DC-BLOG.READ-DRAFT-POSTS
 * @permissionId BLOG.READ_ANY_DRAFT
 * @permissionId BLOG.READ-PUBLICATION
 * @webhook
 * @eventType wix.blog.v3.draft_updated
 * @serviceIdentifier com.wixpress.npm.communities.platformized.blog.v3.DraftPostService
 * @slug updated
 */
export declare function onDraftUpdated(
  handler: (event: DraftUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a draft post.
 *
 * For 3rd-party apps, `memberId` is a required field.
 *
 * In `categoryIds`, only categories that already exist will be added to the draft post. Including
 * `categoryIds` that do not exist will not return an error, but they will not be added to the `categoryIds` array.
 * If some of the categories exist, those will be added while those that do not exist will not be added to `categoryIds`.
 * You can check which categories are available with
 * [List Categories](https://dev.wix.com/docs/rest/business-solutions/blog/category/list-categories).
 * @param draftPost - Draft post to create.
 * @public
 * @requiredField draftPost
 * @requiredField draftPost.title
 * @param options - Options for creating a draft post.
 * @permissionId BLOG.CREATE-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.CreateDraftPost
 */
export async function createDraftPost(
  draftPost: NonNullablePaths<DraftPost, `title`, 2>,
  options?: CreateDraftPostOptions
): Promise<
  NonNullablePaths<
    CreateDraftPostResponse,
    | `draftPost._id`
    | `draftPost.title`
    | `draftPost.categoryIds`
    | `draftPost.hashtags`
    | `draftPost.minutesToRead`
    | `draftPost.tagIds`
    | `draftPost.relatedPostIds`
    | `draftPost.pricingPlanIds`
    | `draftPost.status`
    | `draftPost.moderationDetails.submittedBy`
    | `draftPost.moderationDetails.status`
    | `draftPost.hasUnpublishedChanges`
    | `draftPost.seoData.tags`
    | `draftPost.seoData.tags.${number}.type`
    | `draftPost.seoData.tags.${number}.children`
    | `draftPost.seoData.tags.${number}.custom`
    | `draftPost.seoData.tags.${number}.disabled`
    | `draftPost.seoData.settings.preventAutoRedirect`
    | `draftPost.seoData.settings.keywords`
    | `draftPost.seoData.settings.keywords.${number}.term`
    | `draftPost.seoData.settings.keywords.${number}.isMain`
    | `draftPost.media.embedMedia.thumbnail.url`
    | `draftPost.media.embedMedia.thumbnail.width`
    | `draftPost.media.embedMedia.thumbnail.height`
    | `draftPost.media.embedMedia.video.url`
    | `draftPost.media.embedMedia.video.width`
    | `draftPost.media.embedMedia.video.height`
    | `draftPost.media.displayed`
    | `draftPost.media.custom`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      {
        draftPost: draftPost,
        publish: options?.publish,
        fieldsets: options?.fieldsets,
      },
      ['draftPost.richContent']
    ),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'draftPost.heroImage' },
          { path: 'draftPost.media.wixMedia.image' },
        ],
      },
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [
          { path: 'draftPost.url' },
          { path: 'draftPost.translations.url' },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBlogV3Draft.createDraftPost(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPost.heroImage' },
            { path: 'draftPost.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPost.url' },
            { path: 'draftPost.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPost.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftPost: '$[0]',
          publish: '$[1].publish',
          fieldsets: '$[1].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['draftPost', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDraftPostOptions {
  /**
   * Whether the draft post should be published on creation.
   *
   * Default: `false`
   */
  publish?: boolean;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Creates multiple draft posts.
 * @param draftPosts - Draft posts to create.
 * @public
 * @requiredField draftPosts
 * @requiredField draftPosts.title
 * @param options - Options for creating multiple draft posts.
 * @permissionId BLOG.CREATE-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.BulkCreateDraftPosts
 */
export async function bulkCreateDraftPosts(
  draftPosts: NonNullablePaths<DraftPost, `title`, 2>[],
  options?: BulkCreateDraftPostsOptions
): Promise<
  NonNullablePaths<
    BulkCreateDraftPostsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item._id`
    | `results.${number}.item.title`
    | `results.${number}.item.minutesToRead`
    | `results.${number}.item.status`
    | `results.${number}.item.moderationDetails.submittedBy`
    | `results.${number}.item.moderationDetails.status`
    | `results.${number}.item.hasUnpublishedChanges`
    | `results.${number}.item.media.displayed`
    | `results.${number}.item.media.custom`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      {
        draftPosts: draftPosts,
        publish: options?.publish,
        returnFullEntity: options?.returnFullEntity,
        fieldsets: options?.fieldsets,
      },
      ['draftPosts.richContent']
    ),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'draftPosts.heroImage' },
          { path: 'draftPosts.media.wixMedia.image' },
        ],
      },
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [
          { path: 'draftPosts.url' },
          { path: 'draftPosts.translations.url' },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'draftPosts.media.wixMedia.videoV2' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBlogV3Draft.bulkCreateDraftPosts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.heroImage' },
            { path: 'results.item.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'results.item.url' },
            { path: 'results.item.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.item.media.wixMedia.videoV2' }],
        },
      ]),
      ['results.item.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftPosts: '$[0]',
          publish: '$[1].publish',
          returnFullEntity: '$[1].returnFullEntity',
          fieldsets: '$[1].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['draftPosts', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateDraftPostsOptions {
  /** Whether the draft post should be published after creation. */
  publish?: boolean;
  /** Whether to return the full created draft post entities in the response. */
  returnFullEntity?: boolean;
  /**
   * List of draft post fields to be included in the response if the entities are present.
   * Base default fieldset returns all core draft post properties (all properties that are not a supported fieldset value).
   * For example,  when `URL` fieldset is selected, returned draft post will include the set of base properties and the draft post's preview url.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Updates multiple draft posts.
 * @public
 * @requiredField options.draftPosts.draftPost
 * @requiredField options.draftPosts.draftPost._id
 * @param options - Options for updating multiple draft posts.
 * @permissionId BLOG.EDIT-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.BulkUpdateDraftPosts
 */
export async function bulkUpdateDraftPosts(
  options?: NonNullablePaths<
    BulkUpdateDraftPostsOptions,
    `draftPosts.${number}.draftPost` | `draftPosts.${number}.draftPost._id`,
    5
  >
): Promise<
  NonNullablePaths<
    BulkUpdateDraftPostsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item._id`
    | `results.${number}.item.title`
    | `results.${number}.item.minutesToRead`
    | `results.${number}.item.status`
    | `results.${number}.item.moderationDetails.submittedBy`
    | `results.${number}.item.moderationDetails.status`
    | `results.${number}.item.hasUnpublishedChanges`
    | `results.${number}.item.media.displayed`
    | `results.${number}.item.media.custom`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      {
        draftPosts: options?.draftPosts,
        action: options?.action,
        scheduledPublishDate: options?.scheduledPublishDate,
        returnFullEntity: options?.returnFullEntity,
        fieldsets: options?.fieldsets,
      },
      ['draftPosts.draftPost.richContent']
    ),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'draftPosts.draftPost.heroImage' },
          { path: 'draftPosts.draftPost.media.wixMedia.image' },
        ],
      },
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [
          { path: 'draftPosts.draftPost.url' },
          { path: 'draftPosts.draftPost.translations.url' },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'draftPosts.draftPost.media.wixMedia.videoV2' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBlogV3Draft.bulkUpdateDraftPosts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.heroImage' },
            { path: 'results.item.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'results.item.url' },
            { path: 'results.item.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.item.media.wixMedia.videoV2' }],
        },
      ]),
      ['results.item.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftPosts: '$[0].draftPosts',
          action: '$[0].action',
          scheduledPublishDate: '$[0].scheduledPublishDate',
          returnFullEntity: '$[0].returnFullEntity',
          fieldsets: '$[0].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateDraftPostsOptions {
  /**
   * Draft posts to update.
   * @minSize 1
   * @maxSize 20
   */
  draftPosts?: MaskedDraftPosts[];
  /**
   * Action to perform on the posts.
   *
   * Default: `UPDATE`
   */
  action?: ActionWithLiterals;
  /** Posts' scheduled publish date when `action` is set to `UPDATE_SCHEDULE`. */
  scheduledPublishDate?: Date | null;
  /** Whether to return the full updated draft post entities in the response. */
  returnFullEntity?: boolean;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Retrieves a list of up to 100 deleted draft posts.
 *
 * List Draft Posts runs with these defaults, which you can override:
 * - `editedDate` is sorted in descending order. In this case,`editedDate` implies the date the post was deleted.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 * @public
 * @param options - Options for listing deleted draft posts.
 * @permissionId BLOG.READ_DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.ListDeletedDraftPosts
 */
export async function listDeletedDraftPosts(
  options?: ListDeletedDraftPostsOptions
): Promise<
  NonNullablePaths<
    ListDeletedDraftPostsResponse,
    | `draftPosts`
    | `draftPosts.${number}._id`
    | `draftPosts.${number}.title`
    | `draftPosts.${number}.minutesToRead`
    | `draftPosts.${number}.status`
    | `draftPosts.${number}.moderationDetails.submittedBy`
    | `draftPosts.${number}.moderationDetails.status`
    | `draftPosts.${number}.hasUnpublishedChanges`
    | `draftPosts.${number}.seoData.settings.preventAutoRedirect`
    | `draftPosts.${number}.media.displayed`
    | `draftPosts.${number}.media.custom`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    {
      language: options?.language,
      sort: options?.sort,
      paging: options?.paging,
      fieldsets: options?.fieldsets,
      draftPostIds: options?.draftPostIds,
    },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.listDeletedDraftPosts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPosts.heroImage' },
            { path: 'draftPosts.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPosts.url' },
            { path: 'draftPosts.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPosts.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPosts.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          language: '$[0].language',
          sort: '$[0].sort',
          paging: '$[0].paging',
          fieldsets: '$[0].fieldsets',
          draftPostIds: '$[0].draftPostIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListDeletedDraftPostsOptions {
  /**
   * Language filter.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * If omitted, deleted draft posts in all languages are returned.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Sorting options.
   *
   * Default: `EDITING_DATE_DESCENDING`
   */
  sort?: GetDraftPostsSortWithLiterals;
  /** Pagination options. */
  paging?: BlogPaging;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
  /**
   * Draft post ids.
   * @maxSize 100
   * @format GUID
   */
  draftPostIds?: string[];
}

/**
 * Retrieves a draft post by the provided ID.
 * @param draftPostId - Draft post ID.
 * @public
 * @requiredField draftPostId
 * @param options - Options for getting a draft post.
 * @permissionId BLOG.READ_DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.GetDraftPost
 */
export async function getDraftPost(
  draftPostId: string,
  options?: GetDraftPostOptions
): Promise<
  NonNullablePaths<
    GetDraftPostResponse,
    | `draftPost._id`
    | `draftPost.title`
    | `draftPost.categoryIds`
    | `draftPost.hashtags`
    | `draftPost.minutesToRead`
    | `draftPost.tagIds`
    | `draftPost.relatedPostIds`
    | `draftPost.pricingPlanIds`
    | `draftPost.status`
    | `draftPost.moderationDetails.submittedBy`
    | `draftPost.moderationDetails.status`
    | `draftPost.hasUnpublishedChanges`
    | `draftPost.seoData.tags`
    | `draftPost.seoData.tags.${number}.type`
    | `draftPost.seoData.tags.${number}.children`
    | `draftPost.seoData.tags.${number}.custom`
    | `draftPost.seoData.tags.${number}.disabled`
    | `draftPost.seoData.settings.preventAutoRedirect`
    | `draftPost.seoData.settings.keywords`
    | `draftPost.seoData.settings.keywords.${number}.term`
    | `draftPost.seoData.settings.keywords.${number}.isMain`
    | `draftPost.media.embedMedia.thumbnail.url`
    | `draftPost.media.embedMedia.thumbnail.width`
    | `draftPost.media.embedMedia.thumbnail.height`
    | `draftPost.media.embedMedia.video.url`
    | `draftPost.media.embedMedia.video.width`
    | `draftPost.media.embedMedia.video.height`
    | `draftPost.media.displayed`
    | `draftPost.media.custom`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { draftPostId: draftPostId, fieldsets: options?.fieldsets },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.getDraftPost(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPost.heroImage' },
            { path: 'draftPost.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPost.url' },
            { path: 'draftPost.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPost.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftPostId: '$[0]',
          fieldsets: '$[1].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['draftPostId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetDraftPostOptions {
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Updates a draft post.
 * @param _id - Draft post ID.
 * @public
 * @requiredField _id
 * @requiredField draftPost
 * @param options - Options for updating a draft post.
 * @param draftPost - Draft Post info.
 * @permissionId BLOG.EDIT-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.UpdateDraftPost
 */
export async function updateDraftPost(
  _id: string,
  draftPost: UpdateDraftPost,
  options?: UpdateDraftPostOptions
): Promise<
  NonNullablePaths<
    UpdateDraftPostResponse,
    | `draftPost._id`
    | `draftPost.title`
    | `draftPost.categoryIds`
    | `draftPost.hashtags`
    | `draftPost.minutesToRead`
    | `draftPost.tagIds`
    | `draftPost.relatedPostIds`
    | `draftPost.pricingPlanIds`
    | `draftPost.status`
    | `draftPost.moderationDetails.submittedBy`
    | `draftPost.moderationDetails.status`
    | `draftPost.hasUnpublishedChanges`
    | `draftPost.seoData.tags`
    | `draftPost.seoData.tags.${number}.type`
    | `draftPost.seoData.tags.${number}.children`
    | `draftPost.seoData.tags.${number}.custom`
    | `draftPost.seoData.tags.${number}.disabled`
    | `draftPost.seoData.settings.preventAutoRedirect`
    | `draftPost.seoData.settings.keywords`
    | `draftPost.seoData.settings.keywords.${number}.term`
    | `draftPost.seoData.settings.keywords.${number}.isMain`
    | `draftPost.media.embedMedia.thumbnail.url`
    | `draftPost.media.embedMedia.thumbnail.width`
    | `draftPost.media.embedMedia.thumbnail.height`
    | `draftPost.media.embedMedia.video.url`
    | `draftPost.media.embedMedia.video.width`
    | `draftPost.media.embedMedia.video.height`
    | `draftPost.media.displayed`
    | `draftPost.media.custom`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest(
      {
        draftPost: { ...draftPost, id: _id },
        action: options?.action,
        scheduledPublishDate: options?.scheduledPublishDate,
        fieldsets: options?.fieldsets,
      },
      ['draftPost.richContent']
    ),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'draftPost.heroImage' },
          { path: 'draftPost.media.wixMedia.image' },
        ],
      },
      {
        transformFn: transformSDKPageURLToRESTPageURL,
        paths: [
          { path: 'draftPost.url' },
          { path: 'draftPost.translations.url' },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBlogV3Draft.updateDraftPost(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPost.heroImage' },
            { path: 'draftPost.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPost.url' },
            { path: 'draftPost.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPost.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { draftPost: '$[1]' },
        explicitPathsToArguments: {
          'draftPost.id': '$[0]',
          action: '$[2].action',
          scheduledPublishDate: '$[2].scheduledPublishDate',
          fieldsets: '$[2].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'draftPost', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateDraftPost {
  /**
   * Draft post ID.
   * @readonly
   * @maxLength 38
   */
  _id?: string;
  /**
   * Draft post title.
   * @maxLength 200
   */
  title?: string;
  /**
   * Draft post excerpt.
   *
   * If no excerpt has been manually set, an excerpt is automatically generated from the post's text.
   * This can be retrieved using the `GENERATED_EXCERPT` fieldset.
   * @maxLength 500
   */
  excerpt?: string | null;
  /** Whether the draft post is marked as featured. */
  featured?: boolean | null;
  /**
   * Category IDs of the draft post.
   * @maxSize 10
   * @maxLength 38
   */
  categoryIds?: string[];
  /**
   * Draft post owner's member ID.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Hashtags in the post.
   * @maxSize 100
   * @maxLength 100
   */
  hashtags?: string[];
  /** Whether commenting on the draft post is enabled. */
  commentingEnabled?: boolean | null;
  /**
   * Estimated reading time of the draft post (calculated automatically).
   * @readonly
   */
  minutesToRead?: number;
  /** Image placed at the top of the blog page. */
  heroImage?: string;
  /**
   * Tag IDs the draft post is tagged with.
   * @maxSize 30
   * @maxLength 38
   */
  tagIds?: string[];
  /**
   * IDs of posts related to this draft post.
   * @maxSize 3
   * @maxLength 38
   */
  relatedPostIds?: string[];
  /**
   * Pricing plan IDs. Only relevant if a post is assigned to a specific pricing plan. See the Pricing Plans API for more info.
   * @maxSize 100
   * @format GUID
   */
  pricingPlanIds?: string[];
  /**
   * ID of the draft post's translations.
   *
   * All translations of a single post share the same `translationId`.
   * Available only if the [Multilingual](https://support.wix.com/en/article/wix-multilingual-an-overview) app is installed.
   * @format GUID
   */
  translationId?: string | null;
  /**
   * Language the draft post is written in.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Draft Post rich content.
   *
   * <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="image.gallery.video.audio.divider.linkButton.html.giphy.codeBlock.file.hashtag.lineSpacing.indent.link.textColor.textHighlight.heading.verticalEmbed.table.collapsibleList.poll" exampleid="72c23a25-524f-4f70-a260-4a2777b6f5d5">
   * <a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>
   * </widget>
   */
  richContent?: RichContent;
  /**
   * Status of the draft post.
   * @readonly
   */
  status?: StatusWithLiterals;
  /** Details of the draft post in review. Only relevant to posts submitted by guest writers. */
  moderationDetails?: ModerationDetails;
  /**
   * Indicates if there are changes made to the draft post that have not yet been published.
   * @readonly
   */
  hasUnpublishedChanges?: boolean;
  /**
   * Date the draft post was last edited.
   * @readonly
   */
  editedDate?: Date | null;
  /**
   * Date the draft post is scheduled to be published.
   * @readonly
   */
  scheduledPublishDate?: Date | null;
  /** Date the post was first published. */
  firstPublishedDate?: Date | null;
  /** SEO data. */
  seoData?: SeoSchema;
  /**
   * Draft post URL preview. What the URL will look like once the post is published.
   * @readonly
   */
  url?: string;
  /**
   * Date the draft post was first created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * SEO slug.
   * @maxLength 100
   */
  seoSlug?: string | null;
  /** Post cover media. */
  media?: Media;
  /** Number of paragraphs to display in a paid content preview for non-paying users. */
  previewTextParagraph?: number | null;
}

export interface UpdateDraftPostOptions {
  /**
   * Action to perform on the post.
   *
   * Default: `UPDATE`
   */
  action?: ActionWithLiterals;
  /** Post publish schedule date if `action` is set to `UPDATE_SCHEDULE`. */
  scheduledPublishDate?: Date | null;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Moves a draft post to the trash bin.
 * A published post can also be deleted by the `post.id`. See the [Posts API](https://dev.wix.com/docs/rest/business-solutions/blog/posts-stats/post-object) for more information about posts.
 *
 * To permanently delete a post bypassing the trash bin, set the `permanent` field value to `true`. The post can't be restored after this.
 * @param draftPostId - Draft post ID.
 * @public
 * @requiredField draftPostId
 * @param options - Options for deleting a draft post.
 * @permissionId BLOG.DELETE-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.DeleteDraftPost
 */
export async function deleteDraftPost(
  draftPostId: string,
  options?: DeleteDraftPostOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { draftPostId: draftPostId, permanent: options?.permanent },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.deleteDraftPost(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          draftPostId: '$[0]',
          permanent: '$[1].permanent',
        },
        singleArgumentUnchanged: false,
      },
      ['draftPostId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteDraftPostOptions {
  /**
   * Whether to bypass the trash bin and delete the post permanently.
   *
   * Default: `false`
   */
  permanent?: boolean;
}

/**
 * Permanently deletes a draft post that is currently in the trash bin.
 *
 * This action is permanent and can't be reversed.
 * @param draftPostId - Draft post ID.
 * @public
 * @requiredField draftPostId
 * @permissionId BLOG.DELETE-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.RemoveFromTrashBin
 */
export async function removeFromTrashBin(draftPostId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { draftPostId: draftPostId },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.removeFromTrashBin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftPostId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftPostId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes multiple draft posts.
 * @param postIds - Post IDs.
 * @public
 * @requiredField postIds
 * @param options - Options for deleting multiple draft posts.
 * @permissionId BLOG.DELETE-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.BulkDeleteDraftPosts
 */
export async function bulkDeleteDraftPosts(
  postIds: string[],
  options?: BulkDeleteDraftPostsOptions
): Promise<
  NonNullablePaths<
    BulkDeleteDraftPostsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item._id`
    | `results.${number}.item.title`
    | `results.${number}.item.minutesToRead`
    | `results.${number}.item.status`
    | `results.${number}.item.moderationDetails.submittedBy`
    | `results.${number}.item.moderationDetails.status`
    | `results.${number}.item.hasUnpublishedChanges`
    | `results.${number}.item.media.displayed`
    | `results.${number}.item.media.custom`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { postIds: postIds, permanent: options?.permanent },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.bulkDeleteDraftPosts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.heroImage' },
            { path: 'results.item.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'results.item.url' },
            { path: 'results.item.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.item.media.wixMedia.videoV2' }],
        },
      ]),
      ['results.item.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          postIds: '$[0]',
          permanent: '$[1].permanent',
        },
        singleArgumentUnchanged: false,
      },
      ['postIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDeleteDraftPostsOptions {
  /** Should delete bypassing the trash-bin. */
  permanent?: boolean;
}

/**
 * Retrieves a list of up to 100 draft posts per request.
 *
 * List Draft Posts runs with these defaults, which you can override:
 * - `editedDate` is sorted in descending order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 * @public
 * @param options - Options for listing multiple draft posts.
 * @permissionId BLOG.READ_DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.ListDraftPosts
 */
export async function listDraftPosts(
  options?: ListDraftPostsOptions
): Promise<
  NonNullablePaths<
    ListDraftPostsResponse,
    | `draftPosts`
    | `draftPosts.${number}._id`
    | `draftPosts.${number}.title`
    | `draftPosts.${number}.minutesToRead`
    | `draftPosts.${number}.status`
    | `draftPosts.${number}.moderationDetails.submittedBy`
    | `draftPosts.${number}.moderationDetails.status`
    | `draftPosts.${number}.hasUnpublishedChanges`
    | `draftPosts.${number}.seoData.settings.preventAutoRedirect`
    | `draftPosts.${number}.media.displayed`
    | `draftPosts.${number}.media.custom`
    | `metaData.count`
    | `metaData.offset`
    | `metaData.total`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    {
      status: options?.status,
      language: options?.language,
      sort: options?.sort,
      paging: options?.paging,
      fieldsets: options?.fieldsets,
    },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.listDraftPosts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPosts.heroImage' },
            { path: 'draftPosts.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPosts.url' },
            { path: 'draftPosts.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPosts.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPosts.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          status: '$[0].status',
          language: '$[0].language',
          sort: '$[0].sort',
          paging: '$[0].paging',
          fieldsets: '$[0].fieldsets',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListDraftPostsOptions {
  /**
   * Draft post status filter.
   *
   * If omitted, draft posts with all statuses are returned.
   */
  status?: StatusWithLiterals;
  /**
   * Language filter.
   *
   * 2-or-4-letter language code in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * If omitted, draft posts in all languages are returned.
   * @format LANGUAGE_TAG
   */
  language?: string | null;
  /**
   * Sort options.
   *
   * Default: `EDITING_DATE_DESCENDING`
   */
  sort?: GetDraftPostsSortWithLiterals;
  /** Pagination options. */
  paging?: BlogPaging;
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[];
}

/**
 * Retrieves a deleted draft post from the trash bin by the provided ID.
 * @param draftPostId - Draft post ID.
 * @public
 * @requiredField draftPostId
 * @permissionId BLOG.READ_DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.GetDeletedDraftPost
 */
export async function getDeletedDraftPost(
  draftPostId: string
): Promise<
  NonNullablePaths<
    GetDeletedDraftPostResponse,
    | `draftPost._id`
    | `draftPost.title`
    | `draftPost.categoryIds`
    | `draftPost.hashtags`
    | `draftPost.minutesToRead`
    | `draftPost.tagIds`
    | `draftPost.relatedPostIds`
    | `draftPost.pricingPlanIds`
    | `draftPost.status`
    | `draftPost.moderationDetails.submittedBy`
    | `draftPost.moderationDetails.status`
    | `draftPost.hasUnpublishedChanges`
    | `draftPost.seoData.tags`
    | `draftPost.seoData.tags.${number}.type`
    | `draftPost.seoData.tags.${number}.children`
    | `draftPost.seoData.tags.${number}.custom`
    | `draftPost.seoData.tags.${number}.disabled`
    | `draftPost.seoData.settings.preventAutoRedirect`
    | `draftPost.seoData.settings.keywords`
    | `draftPost.seoData.settings.keywords.${number}.term`
    | `draftPost.seoData.settings.keywords.${number}.isMain`
    | `draftPost.media.embedMedia.thumbnail.url`
    | `draftPost.media.embedMedia.thumbnail.width`
    | `draftPost.media.embedMedia.thumbnail.height`
    | `draftPost.media.embedMedia.video.url`
    | `draftPost.media.embedMedia.video.width`
    | `draftPost.media.embedMedia.video.height`
    | `draftPost.media.displayed`
    | `draftPost.media.custom`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { draftPostId: draftPostId },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.getDeletedDraftPost(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPost.heroImage' },
            { path: 'draftPost.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPost.url' },
            { path: 'draftPost.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPost.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftPostId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftPostId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Restores a deleted draft post from the trash bin by the provided ID.
 * @param draftPostId - Draft post ID.
 * @public
 * @requiredField draftPostId
 * @permissionId BLOG.EDIT-DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.RestoreFromTrashBin
 */
export async function restoreFromTrashBin(
  draftPostId: string
): Promise<
  NonNullablePaths<
    RestoreFromTrashBinResponse,
    | `draftPost._id`
    | `draftPost.title`
    | `draftPost.categoryIds`
    | `draftPost.hashtags`
    | `draftPost.minutesToRead`
    | `draftPost.tagIds`
    | `draftPost.relatedPostIds`
    | `draftPost.pricingPlanIds`
    | `draftPost.status`
    | `draftPost.moderationDetails.submittedBy`
    | `draftPost.moderationDetails.status`
    | `draftPost.hasUnpublishedChanges`
    | `draftPost.seoData.tags`
    | `draftPost.seoData.tags.${number}.type`
    | `draftPost.seoData.tags.${number}.children`
    | `draftPost.seoData.tags.${number}.custom`
    | `draftPost.seoData.tags.${number}.disabled`
    | `draftPost.seoData.settings.preventAutoRedirect`
    | `draftPost.seoData.settings.keywords`
    | `draftPost.seoData.settings.keywords.${number}.term`
    | `draftPost.seoData.settings.keywords.${number}.isMain`
    | `draftPost.media.embedMedia.thumbnail.url`
    | `draftPost.media.embedMedia.thumbnail.width`
    | `draftPost.media.embedMedia.thumbnail.height`
    | `draftPost.media.embedMedia.video.url`
    | `draftPost.media.embedMedia.video.width`
    | `draftPost.media.embedMedia.video.height`
    | `draftPost.media.displayed`
    | `draftPost.media.custom`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { draftPostId: draftPostId },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.restoreFromTrashBin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPost.heroImage' },
            { path: 'draftPost.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPost.url' },
            { path: 'draftPost.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPost.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPost.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftPostId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftPostId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of up to 100 draft posts.
 *
 *
 * The `queryDraftPosts()` function builds a query to retrieve a list of up to 100 draft posts, and returns a `DraftPostsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `DraftPostsQueryBuilder` functions onto the query. `DraftPostsQueryBuilder` functions enable you to sort, filter, and control the results that `queryDraftPosts()` returns.
 *
 * `queryDraftPosts()` runs with these `DraftPostsQueryBuilder` defaults that can be overridden:
 * - `limit(50)`
 * - `ascending('_id')`
 *
 * The following `DraftPostsQueryBuilder` functions are supported for `queryDraftPosts()`. For a full description of the Draft Posts object, see the object returned for the `items` property in `DraftPostsQueryResult`.
 * @public
 * @param options - Options for querying draft posts.
 * @permissionId BLOG.READ_DRAFT
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.QueryDraftPosts
 */
export function queryDraftPosts(
  options?: QueryDraftPostsOptions
): DraftPostsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    DraftPost,
    'CURSOR',
    QueryDraftPostsRequest,
    QueryDraftPostsResponse
  >({
    func: async (payload: QueryDraftPostsRequest) => {
      const reqOpts = ambassadorWixBlogV3Draft.queryDraftPosts({
        ...payload,
        ...(options ?? {}),
      });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryDraftPostsRequest['query']) => {
      const args = [query, options] as [
        QueryDraftPostsRequest['query'],
        QueryDraftPostsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest(
        { ...args?.[1], query: args?.[0] },
        []
      );
    },
    responseTransformer: ({ data }: HttpResponse<QueryDraftPostsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'draftPosts.heroImage' },
              { path: 'draftPosts.media.wixMedia.image' },
            ],
          },
          {
            transformFn: transformRESTPageURLToSDKPageURL,
            paths: [
              { path: 'draftPosts.url' },
              { path: 'draftPosts.translations.url' },
            ],
          },
          {
            transformFn: transformRESTVideoV2ToSDKVideoV2,
            paths: [{ path: 'draftPosts.media.wixMedia.videoV2' }],
          },
        ]),
        ['draftPosts.richContent']
      );

      return {
        items: transformedData?.draftPosts,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

export interface QueryDraftPostsOptions {
  /**
   * List of additional draft post fields to include in the response. For example, use the `URL` fieldset to retrieve the url field in
   * the response in addition to the draft post's base fields. Base fields don’t include any of the supported fieldset values. By default
   * only the draft post's base fields are returned.
   * @maxSize 10
   */
  fieldsets?: FieldWithLiterals[] | undefined;
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface DraftPostsQueryResult extends QueryCursorResult {
  items: DraftPost[];
  query: DraftPostsQueryBuilder;
  next: () => Promise<DraftPostsQueryResult>;
  prev: () => Promise<DraftPostsQueryResult>;
}

export interface DraftPostsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'title'
      | 'excerpt'
      | 'featured'
      | 'commentingEnabled'
      | 'minutesToRead'
      | 'translationId'
      | 'language'
      | 'status'
      | 'hasUnpublishedChanges'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'title'
      | 'excerpt'
      | 'featured'
      | 'commentingEnabled'
      | 'minutesToRead'
      | 'translationId'
      | 'language'
      | 'status'
      | 'hasUnpublishedChanges'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | 'title'
      | 'excerpt'
      | 'minutesToRead'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | 'title'
      | 'excerpt'
      | 'minutesToRead'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | 'title'
      | 'excerpt'
      | 'minutesToRead'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | 'title'
      | 'excerpt'
      | 'minutesToRead'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: 'title' | 'excerpt',
    value: string
  ) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: string, value: any[]) => DraftPostsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (propertyName: string, value: any[]) => DraftPostsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'title'
      | 'excerpt'
      | 'minutesToRead'
      | 'translationId'
      | 'language'
      | 'status'
      | 'editedDate'
      | 'scheduledPublishDate',
    value: any
  ) => DraftPostsQueryBuilder;
  exists: (
    propertyName: 'title' | 'excerpt' | 'translationId' | 'language',
    value: boolean
  ) => DraftPostsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'title'
      | 'excerpt'
      | 'featured'
      | 'commentingEnabled'
      | 'status'
      | 'hasUnpublishedChanges'
      | 'editedDate'
      | 'scheduledPublishDate'
    >
  ) => DraftPostsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'title'
      | 'excerpt'
      | 'featured'
      | 'commentingEnabled'
      | 'status'
      | 'hasUnpublishedChanges'
      | 'editedDate'
      | 'scheduledPublishDate'
    >
  ) => DraftPostsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => DraftPostsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => DraftPostsQueryBuilder;
  find: () => Promise<DraftPostsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.QueryDraftPosts
 * @requiredField query
 */
export async function typedQueryDraftPosts(
  query: DraftPostQuery,
  options?: QueryDraftPostsOptions
): Promise<
  NonNullablePaths<
    QueryDraftPostsResponse,
    | `draftPosts`
    | `draftPosts.${number}._id`
    | `draftPosts.${number}.title`
    | `draftPosts.${number}.minutesToRead`
    | `draftPosts.${number}.status`
    | `draftPosts.${number}.moderationDetails.submittedBy`
    | `draftPosts.${number}.moderationDetails.status`
    | `draftPosts.${number}.hasUnpublishedChanges`
    | `draftPosts.${number}.seoData.settings.preventAutoRedirect`
    | `draftPosts.${number}.media.displayed`
    | `draftPosts.${number}.media.custom`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { query: query, ...options },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.queryDraftPosts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'draftPosts.heroImage' },
            { path: 'draftPosts.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'draftPosts.url' },
            { path: 'draftPosts.translations.url' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'draftPosts.media.wixMedia.videoV2' }],
        },
      ]),
      ['draftPosts.richContent']
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DraftPostQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id'];
      operators: ['$eq', '$in', '$ne', '$nin'];
      sort: 'BOTH';
    },
    {
      fields: ['excerpt', 'title'];
      operators: [
        '$eq',
        '$exists',
        '$gt',
        '$gte',
        '$in',
        '$lt',
        '$lte',
        '$ne',
        '$startsWith'
      ];
      sort: 'BOTH';
    },
    {
      fields: ['status'];
      operators: ['$eq', '$in', '$ne', '$nin'];
      sort: 'BOTH';
    },
    {
      fields: ['editedDate', 'scheduledPublishDate'];
      operators: ['$eq', '$gt', '$gte', '$in', '$lt', '$lte', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['categoryIds', 'hashtags', 'pricingPlanIds', 'tagIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['memberId'];
      operators: [];
      sort: 'NONE';
    },
    {
      fields: ['commentingEnabled', 'featured', 'hasUnpublishedChanges'];
      operators: ['$eq', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['minutesToRead'];
      operators: ['$eq', '$gt', '$gte', '$in', '$lt', '$lte', '$ne'];
      sort: 'NONE';
    },
    {
      fields: ['language', 'translationId'];
      operators: ['$eq', '$exists', '$in', '$ne', '$nin'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  DraftPost,
  DraftPostQuerySpec
>;
export type DraftPostQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 2000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 3 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<DraftPost, DraftPostQuerySpec, DraftPostQuery>(),
  },
};

/**
 * Publishes a draft post by ID. This creates a new [post](https://dev.wix.com/docs/rest/business-solutions/blog/posts-stats/post-object)
 * with the data from the draft post.
 *
 * If the draft post was already published, the published post will be updated with the latest values
 * from the draft post.
 * @param draftPostId - Draft post ID.
 * @public
 * @requiredField draftPostId
 * @permissionId BLOG.PUBLISH-POST
 * @applicableIdentity APP
 * @fqn com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.PublishDraftPost
 */
export async function publishDraftPost(
  draftPostId: string
): Promise<NonNullablePaths<PublishDraftPostResponse, `postId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest(
    { draftPostId: draftPostId },
    []
  );

  const reqOpts = ambassadorWixBlogV3Draft.publishDraftPost(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data, [])!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { draftPostId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['draftPostId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
